import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useShortcut, DocumentShortcut } from '../context/ShortcutContext';
import { useTranslation } from 'react-i18next';
import i18n from '../i18n';
import { ChevronUp, ChevronDown, Bookmark, Trash2 } from 'lucide-react';
import { SHORTCUT_REORDER_MIME, SHORTCUT_ADD_MIME, getShortcutDragPayload } from '../utils/shortcutDnd';
import { entityNameToTranslationKey, shortcutPathToTranslationKey, registryPathToTranslationKey } from './shortcutNavBarMaps';
import ShortcutTab from './ShortcutTab';
import OpenTabsBar from './OpenTabsBar';

interface ShortcutNavBarProps {
  isPlanHeaderExpanded?: boolean;
  onPlanHeaderToggle?: () => void;
}

const ShortcutNavBar: React.FC<ShortcutNavBarProps> = ({
  isPlanHeaderExpanded = false,
  onPlanHeaderToggle,
}) => {
  const { shortcuts, removeShortcut, clearAllShortcuts, reorderShortcuts, addShortcut, hasShortcut } =
    useShortcut();
  const navigate = useNavigate();
  const location = useLocation();
  const { t, i18n: i18nInstance } = useTranslation();
  const [isAddDragOver, setIsAddDragOver] = useState(false);

  // Force re-render when language changes.
  const currentLanguage = i18nInstance.language || i18n.language;

  // ---- Translation helpers ----

  const translateEntityName = (storedName: string): string => {
    const key = entityNameToTranslationKey[storedName];
    return key ? t(key) : storedName;
  };

  /**
   * Resolve a bookmark label in the current language.
   * Resolution order (first hit wins):
   *   a) shortcutPathToTranslationKey  — manual overrides + non-registry paths
   *   b) registryPathToTranslationKey  — auto-built from MODULE_REGISTRY at module load
   *   c) entityNameToTranslationKey    — legacy fallback keyed on stored name string
   *   d) raw storedName                — last resort (old bookmarks, dynamic doc names)
   */
  const translateShortcutName = (shortcut: DocumentShortcut, storedName: string): string => {
    const manualKey = shortcutPathToTranslationKey[shortcut.path];
    if (manualKey) return t(manualKey);
    const registryKey = registryPathToTranslationKey[shortcut.path];
    if (registryKey) return t(registryKey);
    return translateEntityName(storedName);
  };

  // ---- Navigation & removal (unchanged logic) ----

  const handleNavigate = (shortcut: DocumentShortcut) => {
    navigate(shortcut.path, {
      state: {
        _id: shortcut.documentTypeId,
        code: shortcut.documentCode,
        name: shortcut.documentName,
        category: shortcut.category,
        subCategory: shortcut.subCategory,
        subType: shortcut.subType,
      },
    });
  };

  const handleRemove = (shortcut: DocumentShortcut) => {
    const currentPath = `${location.pathname}${location.search}`;
    const wasActive =
      currentPath === shortcut.path || location.pathname === shortcut.path;
    removeShortcut(shortcut.id);
    if (wasActive) {
      const remaining = shortcuts.filter((s) => s.id !== shortcut.id);
      if (remaining.length > 0) {
        const idx = shortcuts.findIndex((s) => s.id === shortcut.id);
        const target = remaining[Math.max(0, idx - 1)] ?? remaining[0];
        navigate(target.path);
      }
      // When the last bookmark is removed while active, stay on the current page —
      // the screen is still visible in Row 2 (Open Tabs) and navigating to '/' would
      // break the Open Tab's active-tab highlight.
    }
  };

  // ---- Reorder-drop and add-drop handlers on the bar container ----

  const handleBarDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    // Reorder drags over the bar background (not a tab) → move to last position.
    if (e.dataTransfer.types.includes(SHORTCUT_REORDER_MIME)) {
      e.preventDefault();
      e.dataTransfer.dropEffect = 'move';
      return;
    }
    // A menu entry being dragged in to become a new bookmark.
    if (e.dataTransfer.types.includes(SHORTCUT_ADD_MIME)) {
      e.preventDefault();
      e.dataTransfer.dropEffect = 'copy';
      setIsAddDragOver(true);
    }
  };

  const handleBarDragLeave = () => {
    setIsAddDragOver(false);
  };

  const handleBarDrop = (e: React.DragEvent<HTMLDivElement>) => {
    // Reorder drag landing on bar background → move to last position.
    if (e.dataTransfer.types.includes(SHORTCUT_REORDER_MIME)) {
      e.preventDefault();
      const raw = e.dataTransfer.getData(SHORTCUT_REORDER_MIME);
      const sourceIndex = parseInt(raw, 10);
      if (!isNaN(sourceIndex) && shortcuts.length > 0) {
        reorderShortcuts(sourceIndex, shortcuts.length - 1);
      }
      return;
    }

    if (e.dataTransfer.types.includes(SHORTCUT_ADD_MIME)) {
      e.preventDefault();
      setIsAddDragOver(false);
      const payload = getShortcutDragPayload(e.dataTransfer);
      if (!payload || hasShortcut(payload.path)) {
        return;
      }
      // TopNavLink and OpenTabs both may pass location state (doc type _id/code/…).
      const st = payload.state;
      addShortcut({
        documentTypeId: st?._id,
        documentNumber: '',
        documentCode: st?.code ?? payload.label,
        documentName: st?.name ?? payload.label,
        category: st?.category ?? 'nav',
        subCategory: st?.subCategory,
        subType: st?.subType,
        path: payload.path,
      });
    }
  };

  // ---- Bookmarks bar (Row 1) ----
  // Always render — a user with zero bookmarks still needs a drop target.
  const currentPath = `${location.pathname}${location.search}`;
  const isOnPlanningPage = location.pathname.startsWith('/planning/');

  const bookmarksBar = (
    <div
      className={`w-full bg-gray-50 border-b border-gray-200 shadow-sm my-1 ${
        isAddDragOver ? 'ring-2 ring-blue-400 ring-inset' : ''
      }`}
      onDragOver={handleBarDragOver}
      onDragLeave={handleBarDragLeave}
      onDrop={handleBarDrop}
    >
      <div
        role="tablist"
        className="flex items-center gap-1 px-4 py-1.5 overflow-x-auto overflow-y-hidden scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100"
      >
        {/* Bar label with optional "Close all" button */}
        <span className="flex items-center gap-1 px-2 text-xs font-medium text-gray-500 flex-shrink-0 select-none self-center">
          <Bookmark size={14} />
          {t('shortcuts.barLabel')}
          {shortcuts.length > 0 && (
            <button
              onClick={clearAllShortcuts}
              className="ml-1 text-gray-400 hover:text-blue-600 transition-colors duration-150"
              aria-label={t('shortcuts.clearAll')}
              title={t('shortcuts.clearAll')}
            >
              <Trash2 size={12} />
            </button>
          )}
        </span>

        {/* Planning expand/collapse toggle — standalone button before all tabs */}
        {isOnPlanningPage && onPlanHeaderToggle && (
          <button
            onClick={onPlanHeaderToggle}
            className="flex items-center justify-center w-8 h-8 rounded-lg border bg-blue-50 text-blue-700 border-blue-300 hover:bg-blue-100 transition-all duration-200 cursor-pointer flex-shrink-0 self-center mb-1"
            title={isPlanHeaderExpanded ? (t('planning.collapse') || 'Collapse') : (t('planning.expand') || 'Expand')}
          >
            {isPlanHeaderExpanded ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
          </button>
        )}

        {shortcuts.length === 0 && (
          <span className="flex items-center gap-1 text-xs text-gray-400 border border-dashed border-gray-300 rounded-full px-3 py-1 select-none">
            <Bookmark size={12} />
            {t('shortcuts.dropHint')}
          </span>
        )}

        {shortcuts.map((shortcut, index) => {
            const isActive =
              currentPath === shortcut.path || location.pathname === shortcut.path;
            // Key includes language so tabs re-render on locale switch.
            const tabKey = `${shortcut.id}-${currentLanguage}`;

            const isPlanningRoute = shortcut.path.startsWith('/planning/');

            // Compute labels.
            const primaryLabel = translateShortcutName(shortcut, shortcut.documentCode);

            const secondaryLabel =
              isPlanningRoute &&
              shortcut.documentName &&
              shortcut.documentName !== shortcut.documentCode
                ? translateShortcutName(shortcut, shortcut.documentName)
                : undefined;

            const tooltipText = `${translateShortcutName(shortcut, shortcut.documentCode)} - ${
              shortcut.documentNumber ||
              translateShortcutName(shortcut, shortcut.documentName)
            }`;

            return (
              <ShortcutTab
                key={tabKey}
                shortcut={shortcut}
                index={index}
                isActive={isActive}
                primaryLabel={primaryLabel}
                secondaryLabel={secondaryLabel}
                tooltipText={tooltipText}
                onNavigate={handleNavigate}
                onRemove={handleRemove}
                onReorder={reorderShortcuts}
              />
            );
          })}
      </div>
    </div>
  );

  return (
    <>
      {bookmarksBar}
      <OpenTabsBar />
    </>
  );
};

export default ShortcutNavBar;
